import { useMemo } from 'react';
import { $, $2, fH, fN, barColor } from './utils';

/**
 * Cost & Utilization.
 *
 * PTO adjustment (added alongside the daily_pto pipeline):
 *   Raw utilization is billable / contracted, which penalises anyone who was on
 *   holiday — they look idle when they were simply away. Adjusted utilization
 *   removes PTO and public-holiday hours from the denominator:
 *
 *       adj = billable / (contracted − pto_hours − holiday_hours)
 *
 *   The gap between the two columns is the honest read: raw tells you what the
 *   month actually produced, adjusted tells you how the person performed against
 *   the capacity they really had. A large spread means time off, not slack.
 *
 * PTO rows are joined on user_name → pto_monthly.person. That matched 54/56 in
 * July; anyone unmatched simply gets no adjustment (adj === raw), never a wrong one.
 */
export default function CostTab({ users, summary, pto = [] }) {
  const totalGapCost  = users.reduce((s,u)=>s+(u.cost_of_gap||0), 0);
  const totalGapHours = users.reduce((s,u)=>s+(u.gap_hours||0), 0);
  const ratesArr      = users.filter(u=>u.hourly_rate>0);
  const avgRate       = ratesArr.length ? ratesArr.reduce((s,u)=>s+u.hourly_rate,0)/ratesArr.length : 0;
  const ehrArr        = users.filter(u=>u.effective_hourly_rate>0);
  const avgEffRate    = ehrArr.length ? ehrArr.reduce((s,u)=>s+u.effective_hourly_rate,0)/ehrArr.length : 0;
  const salariedCost  = users.filter(u=>!u.is_freelancer).reduce((s,u)=>s+u.total_cost,0);
  const freelanceCost = users.filter(u=>u.is_freelancer).reduce((s,u)=>s+u.total_cost,0);

  // ── PTO join + adjusted utilization ────────────────────────────────────────
  const ptoByName = useMemo(() => {
    const m = {};
    for (const p of pto) {
      m[p.person] = {
        ptoDays:  Number(p.pto_days      || 0),
        ptoHours: Number(p.pto_hours     || 0),
        holHours: Number(p.holiday_hours || 0),
        ptoCost:  Number(p.pto_cost      || 0),
      };
    }
    return m;
  }, [pto]);

  const enriched = useMemo(() => users.map(u => {
    const p         = ptoByName[u.user_name];
    const ptoHours  = p ? p.ptoHours : 0;
    const holHours  = p ? p.holHours : 0;
    const offHours  = ptoHours + holHours;
    const contracted = u.contracted_hours || 0;
    const available  = Math.max(contracted - offHours, 0);
    const rawUtil    = contracted > 0 ? (u.billable_hours || 0) / contracted * 100 : 0;
    const adjUtil    = available  > 0 ? (u.billable_hours || 0) / available  * 100 : rawUtil;
    return {
      ...u,
      _ptoDays: p ? p.ptoDays : 0,
      _ptoCost: p ? p.ptoCost : 0,
      _offHours: offHours,
      _rawUtil: rawUtil,
      _adjUtil: offHours > 0 ? adjUtil : rawUtil,
      _hasPto: offHours > 0,
    };
  }), [users, ptoByName]);

  const totalPtoCost  = enriched.reduce((s,u)=>s+u._ptoCost, 0);
  const totalOffHours = enriched.reduce((s,u)=>s+u._offHours, 0);
  const totContracted = enriched.reduce((s,u)=>s+(u.contracted_hours||0), 0);
  const totBillable   = enriched.reduce((s,u)=>s+(u.billable_hours||0), 0);
  const orgRawUtil    = totContracted > 0 ? totBillable / totContracted * 100 : 0;
  const orgAdjUtil    = (totContracted - totalOffHours) > 0
    ? totBillable / (totContracted - totalOffHours) * 100
    : orgRawUtil;

  const kpis = [
    { label:'Total Labor Cost',  value:$(summary.total_cost),  color:'var(--amber)' },
    { label:'Salaried Cost',     value:$(salariedCost),        color:'var(--text)'  },
    { label:'Freelancer Cost',   value:$(freelanceCost),       color:'var(--purple)'},
    { label:'Gap Cost',          value:$(totalGapCost),        color:'var(--red)',   sub:'unbillable capacity' },
    { label:'Gap Hours',         value:`${fH(totalGapHours)}h`,color:'var(--red)'  },
    { label:'PTO Cost',          value:$(totalPtoCost),        color:'var(--blue)',  sub:'derived, not in labor cost' },
    { label:'Time Off Hours',    value:`${fH(totalOffHours)}h`,color:'var(--blue)',  sub:'PTO + holidays' },
    { label:'Avg Hourly Rate',   value:$2(avgRate),            color:'var(--text)'  },
    { label:'Avg Effective Rate',value:$2(avgEffRate),         color:'var(--amber)', sub:'cost ÷ billable hrs' },
  ];

  // Cost by position
  const byPosition = useMemo(() => {
    const m = {};
    for (const u of users) {
      const p = u.position || 'Unassigned';
      if (!m[p]) m[p] = { cost:0, hours:0, count:0 };
      m[p].cost += u.total_cost; m[p].hours += u.total_hours_worked; m[p].count++;
    }
    return Object.entries(m).sort((a,b)=>b[1].cost-a[1].cost);
  }, [users]);

  const maxPosCost = Math.max(...byPosition.map(p=>p[1].cost), 1);

  // Top gap cost
  const topGap = [...users].filter(u=>u.cost_of_gap>0).sort((a,b)=>(b.cost_of_gap||0)-(a.cost_of_gap||0)).slice(0,10);
  const maxGap = topGap.length ? topGap[0].cost_of_gap : 1;

  const costSorted = [...enriched].sort((a,b)=>b.total_cost-a.total_cost);

  return (
    <>
      {/* KPI cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:12, marginBottom:20 }}>
        {kpis.map(k=>(
          <div key={k.label} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:16, textAlign:'center' }}>
            <div style={{ fontSize:10, textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', marginBottom:4 }}>{k.label}</div>
            <div style={{ fontFamily:'DM Mono,monospace', fontSize:22, fontWeight:700, color:k.color, margin:'6px 0 2px' }}>{k.value}</div>
            {k.sub && <div style={{ fontSize:11, color:'var(--text3)' }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Org-level utilization comparison */}
      {totalOffHours > 0 && (
        <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:12, padding:'16px 20px', marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--text2)', marginBottom:12 }}>
            Utilization — raw vs. adjusted for time off
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>
            <UtilCompare label="Raw" sub="billable ÷ contracted" pct={orgRawUtil} />
            <UtilCompare label="Adjusted" sub="excludes PTO + holidays from capacity" pct={orgAdjUtil} />
          </div>
          <div style={{ fontSize:11, color:'var(--text3)', marginTop:12 }}>
            {fH(totalOffHours)}h of contracted capacity was time off. The spread between these two
            figures is absence, not idleness.
          </div>
        </div>
      )}

      {/* Distribution charts */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
        <BarCard title="Cost by Position">
          {byPosition.slice(0,12).map(([pos,v])=>(
            <BarRow key={pos}
              label={`${pos} (${v.count})`}
              value={$(v.cost)}
              width={v.cost/maxPosCost*100}
              color="var(--amber)"
            />
          ))}
        </BarCard>
        <BarCard title="Top 10 Gap Cost (Unbillable Capacity)">
          {topGap.map(u=>(
            <BarRow key={u.user_name}
              label={u.user_name}
              value={$(u.cost_of_gap)}
              width={(u.cost_of_gap||0)/maxGap*100}
              color="var(--red)"
              valueColor="var(--red)"
            />
          ))}
        </BarCard>
      </div>

      {/* Detail table */}
      <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid var(--border)', background:'var(--card)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'var(--card2)' }}>
              {['#','Name','Position','Type','Contracted','Worked','Billable','PTO','Util %','Adj Util %','Gap','$/hr','Eff. $/hr','Multiplier','Cost','Gap Cost'].map(h=>(
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {costSorted.map((u,i)=>(
              <tr key={u.id} style={{ borderTop:'1px solid var(--border)' }}>
                <td style={monoTd}>{i+1}</td>
                <td style={{ padding:'9px 14px', fontWeight:600 }}>
                  {u.user_name}
                  {u.is_freelancer && <span style={{ fontSize:9, background:'var(--purple-bg)', color:'var(--purple)', borderRadius:3, padding:'1px 5px', fontWeight:600, marginLeft:6 }}>FL</span>}
                </td>
                <td style={{ padding:'9px 14px', fontSize:12, color:'var(--text2)' }}>{u.position}</td>
                <td style={{ padding:'9px 14px', fontSize:11, color:'var(--text3)' }}>{u.cost_type==='fixed (salaried)'?'Salaried':'Freelancer'}</td>
                <td style={monoTd}>{fH(u.contracted_hours)}</td>
                <td style={monoTd}>{fH(u.total_hours_worked)}</td>
                <td style={monoTd}>{fH(u.billable_hours)}</td>
                <td style={{ ...monoTd, color: u._ptoDays ? 'var(--blue)' : 'var(--text3)' }}>
                  {u._ptoDays ? `${u._ptoDays}d` : '—'}
                </td>
                <td style={monoTd}>{fN(u.utilization_pct ?? u._rawUtil, 0)}%</td>
                <td style={{ ...monoTd, fontWeight: u._hasPto ? 700 : 400, color: u._hasPto ? barColor(u._adjUtil) : 'var(--text3)' }}>
                  {u._hasPto ? `${fN(u._adjUtil, 0)}%` : '—'}
                </td>
                <td style={{ ...monoTd, color:(u.gap_hours||0)>40?'var(--red)':'var(--text2)' }}>{fH(u.gap_hours)}</td>
                <td style={monoTd}>{$2(u.hourly_rate)}</td>
                <td style={{ ...monoTd, color:(u.effective_hourly_rate||0)>u.hourly_rate*2?'var(--red)':'var(--text2)' }}>
                  {u.effective_hourly_rate ? $2(u.effective_hourly_rate) : '—'}
                </td>
                <td style={monoTd}>
                  {u.effective_hourly_rate && u.hourly_rate>0
                    ? `${fN(u.effective_hourly_rate/u.hourly_rate, 1)}×`
                    : '—'}
                </td>
                <td style={{ ...monoTd, fontWeight:600 }}>{$(u.total_cost)}</td>
                <td style={{ ...monoTd, color:'var(--red)' }}>{u.cost_of_gap ? $(u.cost_of_gap) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize:11, color:'var(--text3)', marginTop:14, lineHeight:1.6 }}>
        <strong>Adj Util %</strong> removes PTO and public-holiday hours from contracted capacity,
        so time off no longer reads as idle time. Shown only for people with recorded absence;
        everyone else is unchanged. <strong>PTO Cost</strong> is derived from each person&apos;s
        monthly rate and is <em>not</em> included in Total Labor Cost, which uses the salaried
        fixed-cost model — the two are deliberately separate and should not be summed.
      </p>
    </>
  );
}

function UtilCompare({ label, sub, pct }) {
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:6 }}>
        <span style={{ fontSize:12, fontWeight:600 }}>{label}</span>
        <span style={{ fontFamily:'DM Mono,monospace', fontSize:20, fontWeight:700, color:barColor(pct) }}>
          {fN(pct, 1)}%
        </span>
      </div>
      <div style={{ height:8, background:'var(--card2)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${Math.min(pct,100)}%`, height:'100%', borderRadius:4, background:barColor(pct) }} />
      </div>
      <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>{sub}</div>
    </div>
  );
}

function BarCard({ title, children }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:20 }}>
      <h4 style={{ fontSize:13, fontWeight:700, marginBottom:12, color:'var(--text2)' }}>{title}</h4>
      {children}
    </div>
  );
}

function BarRow({ label, value, width, color, valueColor }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12 }}>
      <div style={{ width:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:500 }}>{label}</div>
      <div style={{ flex:1, height:8, background:'var(--card2)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${width}%`, height:'100%', borderRadius:4, background:color }} />
      </div>
      <div style={{ width:80, textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11, color:valueColor||'var(--text2)' }}>{value}</div>
    </div>
  );
}

const thStyle = { padding:'11px 14px', textAlign:'left', fontWeight:600, fontSize:'10.5px', textTransform:'uppercase', letterSpacing:'.7px', color:'var(--text2)', whiteSpace:'nowrap', position:'sticky', top:0, background:'var(--card2)' };
const monoTd = { padding:'9px 14px', fontFamily:'DM Mono,monospace', fontSize:12, whiteSpace:'nowrap' };
